import {
  isBadManifest,
  type DeliveryBundle,
  type DeliveryEvent,
  type DeliveryState,
} from './deliveryState'

/**
 * How many launches in a row may miss their first map frame on one bundle
 * before it is marked bad (spec §3.6).
 */
export const LAUNCH_CRASH_LIMIT = 2

export interface LaunchResult {
  readonly state: DeliveryState
  readonly chosen: DeliveryBundle
  readonly events: readonly DeliveryEvent[]
}

type Rejection = '' | 'missing' | 'incomplete' | 'format' | 'bad'

/**
 * Picks the bundle for this launch (spec §3.6): the newest complete,
 * supported, not-bad bundle among pending, active, last good and bundled.
 * Counts the previous launch's missing first frame, activates a pending
 * bundle, promotes a confirmed active to last good and sets the launch
 * marker. The bundled map is always a candidate and is never marked bad.
 * Port of `delivery.Launch`.
 */
export function launch(input: DeliveryState, supportedFormat: number): LaunchResult {
  let state = input
  const events: DeliveryEvent[] = []
  if (state.marker !== undefined) {
    const failures = state.failures + 1
    const bad =
      failures >= LAUNCH_CRASH_LIMIT && state.marker !== state.bundled.manifest
        ? [...state.bad, state.marker]
        : state.bad
    state = { ...state, failures, bad }
  }
  const reject = (b: DeliveryBundle | undefined): Rejection => {
    if (b === undefined) return 'missing'
    if (!b.complete) return 'incomplete'
    if (b.format > supportedFormat) return 'format'
    if (isBadManifest(state, b.manifest)) return 'bad'
    return ''
  }
  let chosen = state.bundled
  for (const c of [state.pending, state.active, state.lastGood]) {
    if (c !== undefined && reject(c) === '' && c.sequence > chosen.sequence) chosen = c
  }
  if (state.pending !== undefined) {
    const r = reject(state.pending)
    if (r !== '' && r !== 'bad') {
      events.push({
        name: 'map_update_bundle_rejected',
        sequence: state.pending.sequence,
        reason: r,
      })
    }
  }
  if (state.active !== undefined) {
    events.push(...fallbackEvent(state, state.active, chosen, reject(state.active)))
  }
  if (state.pending !== undefined && chosen.manifest === state.pending.manifest) {
    events.push({ name: 'map_update_activated', sequence: chosen.sequence })
  }
  const { pending: _dropped, ...rest } = state
  state = rest
  if (state.active === undefined || state.active.manifest !== chosen.manifest) {
    const lastGood = promoted(state, chosen, reject)
    const { lastGood: _old, ...withoutLastGood } = state
    state = {
      ...withoutLastGood,
      ...(lastGood === undefined ? {} : { lastGood }),
      active: chosen,
      activeConfirmed: false,
    }
  }
  if (state.marker !== chosen.manifest) state = { ...state, failures: 0 }
  state = { ...state, marker: chosen.manifest }
  return { state, chosen, events }
}

/** Reports leaving the active bundle because it broke. */
function fallbackEvent(
  state: DeliveryState,
  active: DeliveryBundle,
  chosen: DeliveryBundle,
  activeRejected: Rejection,
): DeliveryEvent[] {
  const from = active.sequence
  const crashedHere = state.marker === active.manifest
  if (crashedHere && state.failures >= LAUNCH_CRASH_LIMIT && chosen.manifest === active.manifest) {
    return [
      {
        name: 'map_update_fallback_used',
        from,
        to: chosen.sequence,
        reason: 'launch_crash_no_fallback',
      },
    ]
  }
  if (activeRejected === 'bad' && crashedHere) {
    return [{ name: 'map_update_fallback_used', from, to: chosen.sequence, reason: 'launch_crash' }]
  }
  if (activeRejected !== '') {
    return [{ name: 'map_update_fallback_used', from, to: chosen.sequence, reason: activeRejected }]
  }
  return []
}

/**
 * The last good bundle once `chosen` becomes active: the outgoing active if
 * it reached a first frame and is still usable, else the current last good
 * unless that is what becomes active.
 */
function promoted(
  state: DeliveryState,
  chosen: DeliveryBundle,
  reject: (b: DeliveryBundle | undefined) => Rejection,
): DeliveryBundle | undefined {
  if (state.active !== undefined && state.activeConfirmed && reject(state.active) === '') {
    return state.active
  }
  if (
    state.lastGood !== undefined &&
    state.lastGood.manifest !== chosen.manifest &&
    reject(state.lastGood) === ''
  ) {
    return state.lastGood
  }
  return undefined
}
